import { useEffect, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { hasAtLeast, isEmpty, isNullish } from 'remeda'
import { useMediaQuery } from 'usehooks-ts'
import { ComplementaryProductsSlider } from '@components/ComplementaryProductsSlider/ComplementaryProductsSlider'
import { ConditionalShopTheLook } from '@components/ConditionalShopTheLook/ConditionalShopTheLook'
import { RecentProductsSlider } from '@components/RecentProductsSlider/RecentProductsSlider'
import { SimilarProductsSlider } from '@components/SimilarProductsSlider/SimilarProductsSlider'
import { SizeGuideModal } from '@components/SizeGuideModal/SizeGuideModal'
import { SizePicker } from '@components/SizePicker/SizePicker'
import { CustomThemeProvider } from '@helpers'
import { useHeader } from '@velasca/hooks/use-header'
import { useQueryParams } from '@velasca/hooks/use-query-params'
import { useRecentProducts } from '@velasca/hooks/use-recent-products'
import { CraftedWithCareIcon } from '@velasca/icons/crafted-with-care'
import { MadeInItalyIcon } from '@velasca/icons/made-in-italy'
import { ReturnsIcon } from '@velasca/icons/returns'
import { SelectedFactoriesIcon } from '@velasca/icons/selected-factories'
import { DetailsCard } from '@velasca/ui/details-card'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { KlarnaPlacement } from '@velasca/ui/klarna-placement'
import { MediaCarousel } from '@velasca/ui/media-carousel'
import { ProductPage } from '@velasca/ui/product-page'
import { StampedReviews } from '@velasca/ui/stamped-reviews'
import { handleError } from '@velasca/utils/handle-error'
import { ImagesPageProduct } from '../ImagesPageProduct/ImagesPageProduct'
import InfoBlockHeader from '../InfoBlockHeader/InfoBlockHeader'
import MoreDetails from '../MoreDetails'
import type { ApparelProductPageProps } from './ApparelProductPage.types'

const NO_MEDIA_ERROR_MESSAGE = 'There are currently no media for this product.'

export const ApparelProductPage = ({
  product,
  moreDetailsInfo,
  materialSection,
  constructionSection,
  featuredDetailSection,
  contentKitSection,
  collectionBrandSection,
  careSection,
  productInstructions,
  madeInItalySection,
  manufacturingSection,
  packagingSection,
  sizeGuideSection,
  notifyMeTag,
  productMedia,
  complementaryProductSettings = {
    enabled: false,
  },
  relatedProductsSettings = {
    enabled: false,
  },
  recentProductsSettings = {
    enabled: false,
    swapImages: false,
  },
}: ApparelProductPageProps) => {
  const {
    productId,
    url,
    title,
    handle,
    price,
    rawPrice,
    compareAtPrice,
    rawCompareAtPrice,
    notifyMe,
    sizeAndFit,
    description,
    reviews,
    featuredImage,
    medias,
    loading = false,
    sizes,
    pairWithText,
    pairWithProducts,
    collectionBrandID,
    collectionBrandName,
    productType = '',
    seasonName,
    soleDesc,
    relatedSoles,
    shopTheLookIDs,
  } = product

  useEffect(() => {
    if (!sizeGuideSection?.sizeGuide) {
      handleError(`Missing size guide metaobject handle for product ${handle}`)
    }
  }, [handle, sizeGuideSection?.sizeGuide])

  const filteredMedias = useMemo(
    () =>
      medias?.filter((media) => {
        return (
          media.type !== 'image' ||
          (media.type === 'image' &&
            !media.src?.includes('_08.') &&
            !media.src?.includes('_09.') &&
            !media.src?.includes('ZX_') &&
            !media.src?.includes('ZZ_'))
        )
      }),
    [medias],
  )

  useEffect(() => {
    const apparelProductSkeleton = document.querySelector(
      '#apparel-product-skeleton',
    )

    if (apparelProductSkeleton) {
      apparelProductSkeleton.classList.add('react-loaded')
    }
  }, [])

  const [isModalOpen, setIsModalOpen] = useQueryParams<boolean>('size-guide', {
    type: 'boolean',
  })

  useEffect(() => {
    if (!sizeGuideSection?.sizeGuide) {
      setIsModalOpen(false)

      return
    }

    if (!sizeGuideSection.sizeGuide.table) {
      handleError(
        `Missing size guide table metaobject for ${sizeGuideSection.sizeGuide.sizeGuideId}`,
      )
    }
  }, [setIsModalOpen, sizeGuideSection])

  // TODO: this is a really bad pattern. In fact I need to pass skip: true to AVOID making an unnecessary fetch request.
  // Better pattern: create a trackRecent function and call it when needed. Update: the hook already has a setRecentProduct
  // that does exactly what shown below.
  // const { trackRecent } = useRecentProducts()
  // useEffect(() => {
  //   trackRecent(productId)
  // }, [])
  // Another way of doing it would be separating tracking hook from fetching hook. They are two different and loosely related features.
  // const { trackRecent } = useRecentProductTracker()
  // same as above
  // const { recentProducts, loading, error } = useRecentProducts()
  useRecentProducts({ productId, skip: true })

  const promotionalBannerHeight = useHeader(
    (state) => state.promotionalBannerHeight,
  )
  const navbarHeight = useHeader((state) => state.navbarHeight)
  const isDesktop = useMediaQuery('(min-width: 992px)')

  const { t } = useTranslation('product')

  const footerLinks = [
    ...(moreDetailsInfo
      ? [
          {
            link: '#more-details',
            description: t('moreDetailsTitle'),
          },
        ]
      : []),
    ...(materialSection
      ? [
          {
            link: '#material-section',
            description: t('materialTitle'),
          },
        ]
      : []),
    ...(constructionSection
      ? [
          {
            link: '#construction-section',
            description: t('constructionTitle'),
          },
        ]
      : []),
    ...(careSection
      ? [{ link: '#care-section', description: t('careTitle') }]
      : []),
    ...(contentKitSection
      ? [{ link: '#content-section', description: t('contentTitle') }]
      : []),
    ...(productInstructions
      ? [{ link: '#instructions-section', description: t('instructionsTitle') }]
      : []),
  ]

  return (
    <CustomThemeProvider themeID={collectionBrandID}>
      <ProductPage.MainContainer>
        <ProductPage.MainBlock>
          {isDesktop ? (
            <ProductPage.ImageBlock>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {filteredMedias && !isEmpty(filteredMedias) ? (
                  <ImagesPageProduct
                    zoom
                    medias={filteredMedias}
                    loading={loading}
                    color={description?.color}
                    material={description?.composition}
                    productTitle={title}
                    productType={productType}
                    shopTheLookIDs={shopTheLookIDs}
                  />
                ) : (
                  <div>{NO_MEDIA_ERROR_MESSAGE}</div>
                )}
              </ErrorBoundary>
              <ProductPage.ProductSections>
                {collectionBrandSection && !isEmpty(collectionBrandSection) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard
                      {...collectionBrandSection}
                      hasMultipleImages
                      videoUrl={collectionBrandSection.videoUrl}
                      id="collection-brand-section"
                    />
                  </ErrorBoundary>
                )}
                {moreDetailsInfo && !isEmpty(moreDetailsInfo) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <MoreDetails
                      title={t('moreDetailsTitle')}
                      details={moreDetailsInfo}
                    />
                  </ErrorBoundary>
                )}
                {materialSection && !isEmpty(materialSection) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard {...materialSection} id="material-section" />
                  </ErrorBoundary>
                )}
                {constructionSection && !isEmpty(constructionSection) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard
                      {...constructionSection}
                      id="construction-section"
                    />
                  </ErrorBoundary>
                )}
                {featuredDetailSection && !isEmpty(featuredDetailSection) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard
                      {...featuredDetailSection}
                      id="featured-detail-section"
                    />
                  </ErrorBoundary>
                )}
                {careSection && !isEmpty(careSection) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard
                      {...careSection}
                      isCollapsible
                      id="care-section"
                    />
                  </ErrorBoundary>
                )}
                {contentKitSection && !isEmpty(contentKitSection) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard {...contentKitSection} id="content-section" />
                  </ErrorBoundary>
                )}
                {productInstructions && !isEmpty(productInstructions) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard
                      {...productInstructions}
                      id="instructions-section"
                    />
                  </ErrorBoundary>
                )}
                {manufacturingSection && !isEmpty(manufacturingSection) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard
                      {...manufacturingSection}
                      isCollapsible
                      id="manufacturing-section"
                    />
                  </ErrorBoundary>
                )}
                {packagingSection &&
                  !isEmpty(packagingSection) &&
                  !!packagingSection.title &&
                  !!packagingSection.description && (
                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                      <DetailsCard
                        {...packagingSection}
                        isCollapsible
                        id="packaging-section"
                      />
                    </ErrorBoundary>
                  )}
                {madeInItalySection && !isEmpty(madeInItalySection) && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard
                      {...madeInItalySection}
                      id="made-in-italy-section"
                    />
                  </ErrorBoundary>
                )}
              </ProductPage.ProductSections>
            </ProductPage.ImageBlock>
          ) : (
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              {!productMedia || isEmpty(productMedia.slides) ? (
                <div>{NO_MEDIA_ERROR_MESSAGE}</div>
              ) : (
                <ConditionalShopTheLook
                  shopTheLookIDs={shopTheLookIDs}
                  condition={Boolean(
                    shopTheLookIDs && hasAtLeast(shopTheLookIDs, 1),
                  )}
                >
                  <MediaCarousel
                    hasZoom
                    forceBullets
                    bulletsType="squared"
                    media={productMedia}
                  />
                </ConditionalShopTheLook>
              )}
            </ErrorBoundary>
          )}

          <ProductPage.ProductInfoBlock
            headerHeight={promotionalBannerHeight + navbarHeight}
            hasCollectionBrandName={Boolean(collectionBrandName)}
          >
            {handle && !isNullish(price) ? (
              <InfoBlockHeader
                collectionBrandName={collectionBrandName}
                description={description}
                footerLinks={footerLinks}
                handle={handle}
                compareAtPrice={compareAtPrice}
                rawCompareAtPrice={rawCompareAtPrice}
                price={price}
                rawPrice={rawPrice}
                reviews={reviews}
                title={title}
                pairWithText={pairWithText}
                pairWithProducts={pairWithProducts}
                productType={productType}
                seasonName={seasonName}
                relatedSoles={relatedSoles}
                soleDesc={soleDesc}
              />
            ) : null}
            {sizes ? (
              <SizePicker
                product={product}
                price={price}
                setIsModalOpen={setIsModalOpen}
                title={t('sizeTitle')}
                footerTitle={t('footerTitle')}
                footerText={sizeAndFit}
                link={t('sizeLink')}
                sizes={sizes}
                notifyMeTag={notifyMeTag}
                notifyMe={notifyMe}
                showButton={
                  sizeGuideSection?.sizeGuide?.sizeGuideId !== undefined
                }
              />
            ) : null}
            {rawPrice ? <KlarnaPlacement amount={rawPrice} /> : null}
            <ProductPage.GuaranteeIcons>
              <ReturnsIcon />
              <CraftedWithCareIcon />
              <MadeInItalyIcon />
              <SelectedFactoriesIcon />
            </ProductPage.GuaranteeIcons>
          </ProductPage.ProductInfoBlock>

          {!isDesktop && (
            <ProductPage.ProductSections>
              {collectionBrandSection && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard
                    {...collectionBrandSection}
                    hasMultipleImages
                    videoUrl={collectionBrandSection.videoUrl}
                    id="collection-brand-section"
                  />
                </ErrorBoundary>
              )}
              {moreDetailsInfo && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <MoreDetails
                    title={t('moreDetailsTitle')}
                    details={moreDetailsInfo}
                  />
                </ErrorBoundary>
              )}
              {materialSection && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard {...materialSection} id="material-section" />
                </ErrorBoundary>
              )}
              {constructionSection && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard {...constructionSection} id="material-section" />
                </ErrorBoundary>
              )}
              {featuredDetailSection && !isEmpty(featuredDetailSection) && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard
                    {...featuredDetailSection}
                    id="featured-detail-section"
                  />
                </ErrorBoundary>
              )}
              {careSection && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard
                    {...careSection}
                    isCollapsible
                    id="care-section"
                  />
                </ErrorBoundary>
              )}
              {contentKitSection && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard {...contentKitSection} id="content-section" />
                </ErrorBoundary>
              )}
              {productInstructions && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard
                    {...productInstructions}
                    id="instructions-section"
                  />
                </ErrorBoundary>
              )}
              {manufacturingSection && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard
                    {...manufacturingSection}
                    isCollapsible
                    id="manufacturing-section"
                  />
                </ErrorBoundary>
              )}
              {packagingSection &&
                !isEmpty(packagingSection) &&
                !!packagingSection.title &&
                !!packagingSection.description && (
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <DetailsCard
                      {...packagingSection}
                      isCollapsible
                      id="packaging-section"
                    />
                  </ErrorBoundary>
                )}
              {madeInItalySection && (
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <DetailsCard
                    {...madeInItalySection}
                    id="made-in-italy-section"
                  />
                </ErrorBoundary>
              )}
            </ProductPage.ProductSections>
          )}

          {isModalOpen && sizeGuideSection?.sizeGuide && (
            <SizeGuideModal
              {...sizeGuideSection}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
            />
          )}
        </ProductPage.MainBlock>
      </ProductPage.MainContainer>

      <ProductPage.Sliders>
        {complementaryProductSettings.enabled && handle && (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <ComplementaryProductsSlider
              productHandle={handle}
              slidesSize="lg"
            />
          </ErrorBoundary>
        )}
        {relatedProductsSettings.enabled && handle && (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <SimilarProductsSlider productHandle={handle} slidesSize="lg" />
          </ErrorBoundary>
        )}
      </ProductPage.Sliders>

      <ErrorBoundary fallback={null}>
        <StampedReviews
          description={description}
          featuredImage={featuredImage}
          handle={handle}
          productId={productId}
          title={title}
          url={url}
        />
      </ErrorBoundary>

      <ProductPage.Sliders>
        {recentProductsSettings.enabled && (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <RecentProductsSlider
              showProductType
              swapImages={recentProductsSettings.swapImages}
              slidesSize="lg"
            />
          </ErrorBoundary>
        )}
      </ProductPage.Sliders>
    </CustomThemeProvider>
  )
}
